/**
 * DMV Transfer Instructions Document
 * Phase 2 document for vehicle contributions
 * No signatures required - instructional document
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateDMVTransferDocHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 18pt;
      margin-bottom: 24pt;
    }
    h1 {
      font-size: 18pt;
      margin: 0 0 8pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    .subtitle {
      font-size: 10pt;
      font-style: italic;
      color: #666;
    }
    .info-box {
      border: 2px solid #c9a961;
      padding: 16pt;
      margin: 20pt 0;
      background-color: #fafafa;
      border-radius: 4pt;
    }
    .info-label {
      font-weight: bold;
      display: inline-block;
      min-width: 1.5in;
    }
    .section {
      margin: 20pt 0;
    }
    .section-title {
      font-weight: bold;
      font-size: 11pt;
      margin-bottom: 12pt;
      text-transform: uppercase;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    .step {
      margin: 16pt 0;
      padding-left: 20pt;
    }
    .step-number {
      font-weight: bold;
      font-size: 12pt;
      margin-right: 8pt;
      color: #c9a961;
    }
    .checklist-item {
      margin: 8pt 0 8pt 28pt;
      font-size: 10pt;
    }
    .checkbox {
      display: inline-block;
      width: 12pt;
      height: 12pt;
      border: 1px solid #333;
      margin-right: 8pt;
      vertical-align: middle;
    }
    .important-box {
      border: 2px solid #d97706;
      background-color: #fffbeb;
      padding: 14pt;
      margin: 16pt 0;
      border-radius: 4pt;
    }
    .important-title {
      color: #d97706;
      font-weight: bold;
      font-size: 11pt;
      margin-bottom: 8pt;
    }
    .sample-text {
      margin: 10pt 28pt;
      padding: 12pt;
      background-color: #f5f5f5;
      border: 1px solid #ccc;
      font-size: 10pt;
    }
    .footer {
      margin-top: 30pt;
      padding-top: 16pt;
      border-top: 1px solid #ccc;
      text-align: center;
      font-size: 9pt;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Vehicle Transfer Instructions</h1>
    <div class="subtitle">DMV Processing Guidelines for Private Ecclesiastical Transfer</div>
  </div>
  
  <div class="info-box">
    <div><span class="info-label">Vehicle:</span> {{vehicle_description}}</div>
    <div><span class="info-label">VIN:</span> {{vehicle_vin}}</div>
    <div><span class="info-label">Contributor:</span> {{contributor_name}}</div>
    <div><span class="info-label">Receiving Entity:</span> {{receiving_entity}}</div>
    <div><span class="info-label">Association:</span> {{una_name}}</div>
    <div><span class="info-label">Transfer Date:</span> {{contribution_date}}</div>
  </div>
  
  <div class="section">
    <div class="section-title">Purpose of This Document</div>
    <p>
      This document provides step-by-step instructions for processing the private 
      ecclesiastical transfer of the vehicle listed above. This transfer occurs within 
      the private domain of {{una_name}} and is not a commercial sale or public domain 
      transfer.
    </p>
  </div>
  
  <div class="important-box">
    <div class="important-title">⚠️ IMPORTANT NOTICE</div>
    <p style="margin: 0;">
      This is a PRIVATE ECCLESIASTICAL TRANSFER, not a commercial sale. No money has 
      changed hands. This transfer is exempt from documentary transfer taxes and sales 
      taxes under ecclesiastical property transfer exemptions.
    </p>
  </div>
  
  <div class="section">
    <div class="section-title">Step-by-Step Transfer Process</div>
    
    <div class="step">
      <span class="step-number">1.</span>
      <strong>Complete Title Transfer Section</strong>
      <p style="margin: 8pt 0 8pt 28pt; font-size: 10pt;">
        On the vehicle title, complete the transfer section with the following information:
      </p>
      <div class="checklist-item"><span class="checkbox"></span> Seller/Transferor: {{contributor_name}}</div>
      <div class="checklist-item"><span class="checkbox"></span> Buyer/Transferee: {{receiving_entity}}</div>
      <div class="checklist-item"><span class="checkbox"></span> Sale Price: Write "ECCLESIASTICAL CONTRIBUTION - NO SALE"</div>
      <div class="checklist-item"><span class="checkbox"></span> Date of Transfer: {{contribution_date}}</div>
      <div class="checklist-item"><span class="checkbox"></span> Odometer Reading: (current reading)</div>
    </div>
    
    <div class="step">
      <span class="step-number">2.</span>
      <strong>Complete REG 256 Form (Statement of Facts)</strong>
      <p style="margin: 8pt 0 8pt 28pt; font-size: 10pt;">
        On DMV Form REG 256, explain the nature of the transfer:
      </p>
      <div class="sample-text">
        "This vehicle is being transferred as a private member contribution to 
        {{una_name}}, an unincorporated nonprofit association operating within private 
        ecclesiastical jurisdiction. This is not a commercial sale. No consideration 
        has been exchanged. Transfer is made for ecclesiastical purposes under the 
        private domain stewardship of the association."
      </div>
    </div>
    
    <div class="step">
      <span class="step-number">3.</span>
      <strong>Gather Supporting Documents</strong>
      <div class="checklist-item"><span class="checkbox"></span> Signed vehicle title</div>
      <div class="checklist-item"><span class="checkbox"></span> Completed REG 256 form</div>
      <div class="checklist-item"><span class="checkbox"></span> Copy of Private Member Contribution Assignment document</div>
      <div class="checklist-item"><span class="checkbox"></span> Copy of Authorization Resolution (from Phase 1)</div>
      <div class="checklist-item"><span class="checkbox"></span> Copy of Acceptance Resolution (from Phase 2)</div>
      <div class="checklist-item"><span class="checkbox"></span> Smog certificate (if required by state)</div>
    </div>
    
    <div class="step">
      <span class="step-number">4.</span>
      <strong>Submit to DMV</strong>
      <p style="margin: 8pt 0 8pt 28pt; font-size: 10pt;">
        Submit all documents to the Department of Motor Vehicles. Request exemption 
        from use tax based on ecclesiastical transfer status. Be prepared to explain 
        that this is a private ecclesiastical contribution, not a commercial transaction.
      </p>
    </div>
    
    <div class="step">
      <span class="step-number">5.</span>
      <strong>Update Insurance</strong>
      <div class="checklist-item"><span class="checkbox"></span> Contact insurance company</div>
      <div class="checklist-item"><span class="checkbox"></span> Update policy holder to {{receiving_entity}}</div>
      <div class="checklist-item"><span class="checkbox"></span> Provide ecclesiastical status documentation</div>
      <div class="checklist-item"><span class="checkbox"></span> Maintain continuous coverage during transfer</div>
    </div>
  </div>
  
  <div class="section">
    <div class="section-title">Post-Transfer Actions</div>
    <div class="checklist-item"><span class="checkbox"></span> File new title in {{una_name}} records when received</div>
    <div class="checklist-item"><span class="checkbox"></span> Update vehicle inventory in association records</div>
    <div class="checklist-item"><span class="checkbox"></span> Establish maintenance schedule and documentation system</div>
    <div class="checklist-item"><span class="checkbox"></span> Designate authorized drivers and create usage policy</div>
  </div>
  
  <div class="important-box">
    <div class="important-title">📋 RECORD KEEPING</div>
    <p style="margin: 0;">
      Maintain copies of all transfer documents, DMV correspondence, and the new title 
      in the permanent records of {{una_name}}. These documents establish the chain of 
      stewardship for this ecclesiastical asset.
    </p>
  </div>
  
  <div class="footer">
    DMV Transfer Instructions prepared {{current_date}}<br>
    For ecclesiastical vehicle transfer purposes<br>
    {{una_name}}
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
